import dayjs from 'dayjs'
import { useState } from 'react'
import { Spacer } from '~/components/Common/Spacer'
import { responsiveBreakpointMap } from '~/constants'
import { useGetDeviceWidth } from '~/hooks'
import { useAuth } from '../Auth/AuthProvider'
import SubscriptionPopCard from '../Subscription/SubscriptionPopCard'

interface SidebarProps {
    /**
     * Sidebar menu
     */
    children: JSX.Element | JSX.Element[]
    /**
     * Sidebar shrink state
     */
    shrink?: boolean
    /**
     * Responsive breakpoint
     */
    responsive?: keyof typeof responsiveBreakpointMap
    /**
     * Responsive show sidebar state
     */
    showMobileSidebar?: boolean
    /**
     * Prepend element at the start of sidebar
     */
    prepend?: JSX.Element
    /**
     * Append element at the end of sidebar
     */
    append?: JSX.Element
}

export const Sidebar = ({
    children,
    shrink = false,
    responsive = 'sm',
    showMobileSidebar = false,
    prepend,
    append
}: SidebarProps) => {
    const [deviceWidth, setDeviceWidth] = useState(0)
    useGetDeviceWidth(setDeviceWidth)

    const { currentBusiness } = useAuth()
    const business = currentBusiness()

    const isSubscribed = () => {
        try {
            if (!business) {
                return false
            }
            const subscription = business?.subscription
            const hasActiveSubscription = subscription && !dayjs().isAfter(dayjs(subscription.endDate).local())
            return hasActiveSubscription ? true : false
        }
        catch (e) {
            return false
        }
    }

    const isResponsiveView = deviceWidth !== 0 && deviceWidth <= responsiveBreakpointMap[responsive]
    const isShrinkedView = !isResponsiveView && shrink

    return (
        <aside
            className={`flex flex-col flex-shrink-0 overflow-y-hidden border-r rtl:border-r-0 rtl:border-l border-neutrals-100 bg-white h-full transition-all ${isShrinkedView ? 'w-[70px]' : 'w-52'
                } ${isResponsiveView
                    ? `absolute z-30 ${showMobileSidebar ? 'left-0 rtl:right-0 opacity-100' : '-left-64 rtl:-right-64 rtl:left-auto opacity-0'}`
                    : ''
                }`}>
            {prepend}
            <ul className={`overflow-y-auto grid grid-cols-1 gap-2 ${isShrinkedView ? 'py-2' : 'py-2 px-2'}`}>
                {children}
            </ul>

            {
                !isShrinkedView && !isSubscribed() &&
                < SubscriptionPopCard />
            }

            {append && (
                <>
                    <Spacer></Spacer>
                    {append}
                </>
            )}
        </aside>
    )
}
